/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import type { DegreePlan_AcademicTerm as AcademicTerm } from '__generated__/graphql-types';

import { useRetracked } from 'js/lib/retracked';

import { ButtonGroup, CheckboxCard, CheckboxGroup, Dialog, breakpoints } from '@coursera/cds-core';

import { ADD_TERM_TO_PLAN } from 'bundles/degree-home/components/v3-dpm/constants/trackingNameConstants';
import type { TermStore } from 'bundles/degree-home/components/v3-dpm/context/TermStoreContext';
import { useTermStore } from 'bundles/degree-home/components/v3-dpm/context/TermStoreContext';
import type { UseCurriculum_EstimatedAcademicTermFragment as EstimatedAcademicTerm } from 'bundles/degree-home/components/v3-dpm/lib/__queries__/__generated__/dpmSharedFragments';
import { now } from 'bundles/degree-home/components/v3-dpm/lib/otherUtils';
import useCurriculum from 'bundles/degree-home/components/v3-dpm/lib/useCurriculum';
import useDegreePlan from 'bundles/degree-home/components/v3-dpm/lib/useDegreePlan';

import _t from 'i18n!nls/degree-home';

const styles = {
  checkboxGroup: css`
    width: 100%;
  `,

  fullWidthMobileButtons: css`
    ${breakpoints.only('xs')} {
      width: 100%;
      max-width: none;
    }
  `,
};

type AddTermsToPlanningToolDialogContextType = {
  openAddTermsDialog: () => void;
  additionalTerms: Record<string, EstimatedAcademicTerm>;
};

const AddTermsToPlanningToolDialogContext = React.createContext<AddTermsToPlanningToolDialogContextType | undefined>(
  undefined
);

export const useAddTermToPlanningToolDialog = () => {
  const context = React.useContext(AddTermsToPlanningToolDialogContext);
  if (!context) {
    throw new Error('useAddTermToPlanningToolDialog must be used within an AddTermsToPlanningToolDialogContext');
  }
  return context;
};

const currentTime = Number(now());

export const getFutureTermsFromToday = (academicTermsPaginated: EstimatedAcademicTerm[]): EstimatedAcademicTerm[] => {
  const latestPastTermIndex = academicTermsPaginated.findLastIndex(
    (term) => term.linkedTerm && new Date(term.linkedTerm.endDate).getTime() < currentTime
  );

  const firstExceedsAllowedTimeIndex = academicTermsPaginated.findIndex((term) => term.exceedsTimeAllowed);

  const startIndex = latestPastTermIndex >= 0 ? latestPastTermIndex + 1 : 0;
  const endIndex = firstExceedsAllowedTimeIndex >= 0 ? firstExceedsAllowedTimeIndex : academicTermsPaginated.length;

  return academicTermsPaginated.slice(startIndex, endIndex);
};

const inProgressTermsWithRegistrationEnded = (
  academicTermsPaginated: EstimatedAcademicTerm[],
  terms: TermStore | undefined
) => {
  const ids: string[] = [];

  const inProgressTermPlans = academicTermsPaginated.filter(
    (term) =>
      term.linkedTerm &&
      new Date(term.linkedTerm.startDate).getTime() < currentTime &&
      new Date(term.linkedTerm.endDate).getTime() > currentTime
  );

  inProgressTermPlans.forEach((term) => {
    const linkedTermId = (term.linkedTerm as AcademicTerm).id;
    const termEntryInStore = terms?.[linkedTermId] ?? undefined;
    const isRegistrationEnded =
      termEntryInStore?.registrationEndDate && new Date(termEntryInStore.registrationEndDate).getTime() < currentTime;
    if (isRegistrationEnded) {
      ids.push(term.estimatedTermId);
    }
  });

  return ids;
};

export const AddTermsToPlanningToolDialogProvider: React.FC = ({ children }) => {
  const track = useRetracked();

  const curriculumGql = useCurriculum();
  const degreePlanGql = useDegreePlan();
  const { terms } = useTermStore();

  const academicTerms = curriculumGql.data?.Degree?.queryById?.academicTermsPaginated?.elements ?? [];
  const learnerPlan = degreePlanGql.data?.LearnerPlan?.queryPlan?.learnerPlan;

  const [selectedTermIds, setSelectedTermIds] = React.useState<string[]>([]);
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [additionalTerms, setTermsToDisplay] = React.useState<Record<string, EstimatedAcademicTerm>>({});

  const openAddTermsDialog = () => setIsDialogOpen(true);
  const futureTermsFromToday = getFutureTermsFromToday(academicTerms);

  const learnerPlanEstimatedTermIds = learnerPlan?.map((plan) => plan.estimatedTermId) ?? [];
  const estimatedTermIdToAcademicTermMap: Record<string, EstimatedAcademicTerm> = futureTermsFromToday.reduce(
    (currMap, term) => ({ ...currMap, [term.estimatedTermId]: term }),
    {}
  );

  const inProgressTermIdsPastRegistrationEnd = inProgressTermsWithRegistrationEnded(futureTermsFromToday, terms);

  const additionalTermIds = Object.keys(additionalTerms);
  const hiddenTermsList = [
    ...learnerPlanEstimatedTermIds,
    ...additionalTermIds,
    ...inProgressTermIdsPastRegistrationEnd,
  ];

  for (const id of hiddenTermsList) {
    if (estimatedTermIdToAcademicTermMap[id]) {
      delete estimatedTermIdToAcademicTermMap[id];
    }
  }

  const visibleTermsList = Object.values(estimatedTermIdToAcademicTermMap);

  const closeDialog = React.useCallback(() => {
    setSelectedTermIds([]);
    setIsDialogOpen(false);
  }, []);

  const handleTermSelected = (event: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    const currentCollectionId = event.target.value;
    setSelectedTermIds((prevState) => {
      return checked ? [...prevState, currentCollectionId] : prevState.filter((id) => id !== currentCollectionId);
    });
  };

  const handleAddTerm = () => {
    track({
      action: 'click',
      trackingName: ADD_TERM_TO_PLAN,
      trackingData: {
        addedTermIds: selectedTermIds,
      },
    });

    const selectedTermsMap: Record<string, EstimatedAcademicTerm> = {};

    for (const id of selectedTermIds) {
      selectedTermsMap[id] = estimatedTermIdToAcademicTermMap[id];
    }

    setTermsToDisplay((prevState) => {
      return { ...prevState, ...selectedTermsMap };
    });

    closeDialog();
  };

  return (
    <AddTermsToPlanningToolDialogContext.Provider value={{ openAddTermsDialog, additionalTerms }}>
      {children}
      {isDialogOpen && (
        <Dialog open onClose={closeDialog} color="darkBlue" sticky="actions">
          <Dialog.HeadingGroup supportText={_t('Add a term to graduation plan')}>
            {_t('Add academic term')}
          </Dialog.HeadingGroup>
          <Dialog.Content>
            <CheckboxGroup
              onChange={handleTermSelected}
              value={selectedTermIds}
              label={_t('Select the academic terms you want to add:')}
              css={styles.checkboxGroup}
            >
              {visibleTermsList.map((term: EstimatedAcademicTerm) => (
                <CheckboxCard key={term.estimatedTermId} label={term.estimatedTermName} value={term.estimatedTermId} />
              ))}
            </CheckboxGroup>
          </Dialog.Content>
          <Dialog.Actions>
            <ButtonGroup
              primaryButtonProps={{
                children: _t('Add terms'),
                onClick: handleAddTerm,
                css: styles.fullWidthMobileButtons,
                disabled: selectedTermIds.length === 0,
              }}
              alternateButtonProps={{
                children: _t('Cancel'),
                onClick: closeDialog,
                variant: 'secondary',
                css: styles.fullWidthMobileButtons,
              }}
            />
          </Dialog.Actions>
        </Dialog>
      )}
    </AddTermsToPlanningToolDialogContext.Provider>
  );
};
